const PageSpeedAnalysisModal = ({ isOpen, progress, message, status }) => {
  if (!isOpen) return null;

  // Calculate progress percentage
  const progressPercentage = Math.max(0, Math.min(100, progress || 0));
  
  // Get status icons and colors
  const getStatusContent = () => {
    switch (status) {
      case 'error':
        return {
          title: 'Error',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          progressColor: 'bg-red-500'
        };
      case 'complete':
        return {
          title: 'Complete',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          progressColor: 'bg-green-500'
        };
      default:
        return {
          title: 'Analyzing Website',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          progressColor: 'bg-blue-500'
        };
    }
  };
  
  const statusContent = getStatusContent();
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-fadeInUp">
        {/* Header */}
        <div className={`p-5 border-b ${statusContent.borderColor} flex items-start justify-between ${statusContent.bgColor}`}>
          <h2 id="modal-title" className={`text-xl font-bold ${statusContent.textColor}`}>
            {statusContent.title}
          </h2>
          {/* No close button - user shouldn't close during analysis */}
        </div>
        
        {/* Content */}
        <div className="p-5">
          <div className="space-y-4">
            {/* Message */}
            <p className={`text-gray-700 text-center font-medium ${status === 'error' ? 'text-red-700' : ''}`}>
              {message || 'Analyzing website performance...'}
            </p>
            
            {/* Progress bar with animated pulse effect */}
            <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ease-in-out ${statusContent.progressColor} ${progressPercentage < 100 && status !== 'error' ? 'animate-pulse' : ''}`}
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            
            {/* Progress percentage */}
            <p className="text-center text-sm text-gray-500">
              {status === 'error' ? 'Error occurred' : `${progressPercentage}% complete`}
            </p>
            
            {/* Status indicators based on stage */}
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${progressPercentage > 0 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                <span className={`text-sm ${progressPercentage > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                  Initializing analysis
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${progressPercentage > 25 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                <span className={`text-sm ${progressPercentage > 25 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                  Gathering performance data
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${progressPercentage > 50 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                <span className={`text-sm ${progressPercentage > 50 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                  Processing metrics
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${progressPercentage > 75 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                <span className={`text-sm ${progressPercentage > 75 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
                  Finalizing results
                </span>
              </div>
            </div>
            
            {/* Warning message */}
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-yellow-800 text-sm">
                <span className="font-bold">Please don't close this window or navigate away.</span> We're analyzing your website's performance with Google PageSpeed Insights.
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer (only shown when complete or error) */}
        {(status === 'complete' || status === 'error') && (
          <div className="p-5 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`px-4 py-2 ${
                status === 'error' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
              } text-white font-medium text-sm rounded-md transition-colors`}
            >
              {status === 'error' ? 'Try Again' : 'View Results'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageSpeedAnalysisModal;